import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext.js';
import { Layout } from './components/Layout.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { RequireRole, landingRouteForRoles } from './components/RequireRole.js';
import { LoginPage } from './pages/LoginPage.js';
import { AuthAction } from './pages/AuthAction.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { AnalyticsPage } from './pages/AnalyticsPage.js';
import { InsightsPage } from './pages/InsightsPage.js';
import { InsightsCompanyPage } from './pages/InsightsCompanyPage.js';
import { InsightsFirmPage } from './pages/InsightsFirmPage.js';
import { CooloffPage } from './pages/CooloffPage.js';
import { UploadPage } from './pages/UploadPage.js';
import { AnnouncementsPage } from './pages/AnnouncementsPage.js';
import { AnnouncementDetailPage } from './pages/AnnouncementDetailPage.js';
import { ArticlesPage } from './pages/ArticlesPage.js';
import { ReviewQueuePage } from './pages/ReviewQueuePage.js';
import { ReconciliationPage } from './pages/ReconciliationPage.js';
import { ArticleDetailPage } from './pages/ArticleDetailPage.js';
import { SitesPage } from './pages/SitesPage.js';
import { SiteDetailPage } from './pages/SiteDetailPage.js';
import { ToolsPage } from './pages/ToolsPage.js';
import { TriagePage } from './pages/TriagePage.js';
import { ThreadDetailPage } from './pages/ThreadDetailPage.js';
import { EntitiesPage } from './pages/EntitiesPage.js';
import EntityWorkbenchPage from './pages/EntityWorkbenchPage.js';
import { CampaignsPage } from './pages/CampaignsPage.js';
import { PipelinePage } from './pages/PipelinePage.js';
import { IntelligencePage } from './pages/IntelligencePage.js';
import { ImagesPage } from './pages/ImagesPage.js';
import { SocialDraftsPage } from './pages/SocialDraftsPage.js';
import NotificationsQueuePage from './pages/NotificationsQueuePage.js';
import TargetNotificationSettingsPage from './pages/TargetNotificationSettingsPage.js';
import RecipientReviewsPage from './pages/RecipientReviewsPage.js';
import SubscribersPage from './pages/SubscribersPage.js';
import DigestPage from './pages/DigestPage.js';
import MergeCandidatesPage from './pages/MergeCandidatesPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { NoAccessPage } from './pages/NoAccessPage.js';
import { AdminOperatorsPage } from './pages/AdminOperatorsPage.js';
import { LinkedInSettingsPage } from './pages/LinkedInSettingsPage.js';
import { LinkedInCallbackPage } from './pages/LinkedInCallbackPage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Index redirect — sends the operator to their role-appropriate landing.
 * super_admin → /dashboard, social_manager → /social, no roles → /no-access.
 */
function IndexRedirect() {
  const { operator, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-100 border-r-transparent"></div>
      </div>
    );
  }
  if (!operator) return <Navigate to="/no-access" replace />;
  return <Navigate to={landingRouteForRoles(operator.roles)} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/action" element={<AuthAction />} />

              {/* Authenticated routes */}
              <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  {/* Role-agnostic: any authenticated operator (incl. empty roles) */}
                  <Route path="/" element={<IndexRedirect />} />
                  <Route path="/no-access" element={<NoAccessPage />} />
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* social_manager + sales (super_admin passes implicitly) */}
                  <Route element={<RequireRole roles={['social_manager', 'sales', 'data_manager']} />}>
                    <Route path="/social" element={<SocialDraftsPage />} />
                    {/* Review queue — the Articles gallery filtered to held
                        articles (same date-driven display + coverage/prospect
                        pill). All operator roles can see what's held. */}
                    <Route path="/review-queue" element={<ReviewQueuePage />} />
                    <Route path="/reconciliation" element={<ReconciliationPage />} />
                  </Route>

                  {/* Articles + Announcements: sales work them; data_manager
                      READS them for context when validating data (2026-08-21) —
                      the backend now 403s content writes for the data role. */}
                  <Route element={<RequireRole roles={['sales', 'data_manager']} />}>
                    <Route path="/announcements" element={<AnnouncementsPage />} />
                    <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
                    <Route path="/articles" element={<ArticlesPage />} />
                    <Route path="/articles/:id" element={<ArticleDetailPage />} />
                  </Route>

                  {/* data_manager — the data-hygiene surfaces (entity curation,
                      merge review, workbench). Data, never content; super_admin
                      passes implicitly. */}
                  <Route element={<RequireRole roles={['data_manager']} />}>
                    <Route path="/entities" element={<EntitiesPage />} />
                    <Route path="/entities/merge-review" element={<MergeCandidatesPage />} />
                    <Route path="/entities/:id" element={<EntityWorkbenchPage />} />
                  </Route>

                  {/* super_admin only — operator management */}
                  <Route element={<RequireRole roles="super_admin" />}>
                    <Route path="/admin/operators" element={<AdminOperatorsPage />} />
                    {/* LinkedIn Community Management API connection (Milestone A, 2026-09-20).
                        /linkedin/callback is the OAuth redirect URL registered on the app. */}
                    <Route path="/settings/linkedin" element={<LinkedInSettingsPage />} />
                    <Route path="/linkedin/callback" element={<LinkedInCallbackPage />} />
                  </Route>

                  {/* sales (super_admin passes implicitly): Dashboard, Upload,
                      Announcements, Articles, Campaigns, Intelligence, Notifications.
                      Social is in the block above. */}
                  <Route element={<RequireRole roles={['sales']} />}>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/insights" element={<InsightsPage />} />
                    <Route path="/insights/company/:id" element={<InsightsCompanyPage />} />
                    <Route path="/insights/firm/:id" element={<InsightsFirmPage />} />
                    <Route path="/cooloff" element={<CooloffPage />} />
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/campaigns" element={<CampaignsPage />} />
                    <Route path="/intelligence" element={<IntelligencePage />} />
                    <Route path="/threads/:id" element={<ThreadDetailPage />} />
                    <Route path="/notifications" element={<Navigate to="/notifications/queue" replace />} />
                    <Route path="/notifications/queue" element={<NotificationsQueuePage />} />
                    <Route path="/notifications/recipient-reviews" element={<RecipientReviewsPage />} />
                    <Route path="/notifications/subscribers" element={<SubscribersPage />} />
                    <Route path="/notifications/digest" element={<DigestPage />} />
                    <Route path="/notifications/targets/:targetId" element={<TargetNotificationSettingsPage />} />
                    {/* Intelligence redirects */}
                    <Route path="/threads" element={<Navigate to="/intelligence" replace />} />
                    <Route path="/synthesis" element={<Navigate to="/intelligence?tab=synthesis" replace />} />
                  </Route>

                  {/* super_admin only — everything else */}
                  <Route element={<RequireRole roles="super_admin" />}>
                    <Route path="/publishing" element={<SitesPage />} />
                    <Route path="/publishing/:id" element={<SiteDetailPage />} />
                    <Route path="/pipeline" element={<PipelinePage />} />
                    <Route path="/images" element={<ImagesPage />} />
                    <Route path="/tools" element={<ToolsPage />} />
                    <Route path="/triage" element={<TriagePage />} />
                    {/* Redirects from old routes */}
                    <Route path="/sites" element={<Navigate to="/publishing" replace />} />
                    <Route path="/sites/:id" element={<Navigate to="/publishing" replace />} />
                    <Route path="/ingress" element={<Navigate to="/pipeline" replace />} />
                    <Route path="/content-products" element={<Navigate to="/pipeline?tab=products" replace />} />
                    {/* Renames */}
                    <Route path="/notifications/merge-candidates" element={<Navigate to="/entities/merge-review" replace />} />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
